import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import useUserContext from 'shared/users/contexts/useUserContext'
import { useConfirm } from '../../../shared/components/confirm/ConfirmProvider'
import useNewsFeed from './useNewsFeed'
import useMarkNewsRead from './useMarkNewsRead'
import { useNewsPostQuery } from '../api/fetchNewsPost'
import { ensureLastReadAtSeeded, isNewsPostUnread } from '../models/newsLastRead'
import { NewsPost } from '../models/types'
import NewsFeedItem from './NewsFeedItem'
import NewsFormModal from './NewsFormModal'
import NewsTitleBar from './NewsTitleBar'

interface NewsFeedProps {
  hasDeepLink: boolean
  deepLinkedPostId: number | null
}

function NewsFeed({ hasDeepLink, deepLinkedPostId }: Readonly<NewsFeedProps>) {
  const { t } = useTranslation()
  const { confirm } = useConfirm()
  const { user, isAdmin } = useUserContext()
  const {
    posts,
    hasMore,
    loadMore,
    isLoading,
    isFetchingNextPage,
    isError,
    isFetching,
    isSuccess,
    refetch,
    addPost,
    updatePost,
    invalidateNews,
    deletePost,
    setPinned,
  } = useNewsFeed()
  const [isModalOpen, setModalOpen] = useState<boolean>(false)
  const [editingPost, setEditingPost] = useState<NewsPost | null>(null)
  // A snapshot, not the live value: the visit marks the feed read, and the posts the reader came
  // for must stay marked new until they leave.
  const [lastReadAtSnapshot] = useState<string | null>(() => (user ? ensureLastReadAtSeeded(user.id) : null))
  // One code path regardless of whether the post also happens to sit on a loaded page: always
  // fetched by id, always rendered above a feed that always starts at page 0.
  const deepLinkedPost = useNewsPostQuery(deepLinkedPostId)
  useMarkNewsRead(user?.id, isSuccess)

  // Filtered on the requested id rather than the resolved one: the feed can arrive from cache
  // before the by-id query does, and until then the post would render both above and below.
  const feedPosts = posts.filter((post) => post.id !== deepLinkedPostId)

  const handleDelete = async (id: number) => {
    const confirmed = await confirm({ message: t('news.deleteConfirm'), danger: true })
    if (confirmed) {
      await deletePost(id)
    }
  }

  const handleAddClick = () => {
    setEditingPost(null)
    setModalOpen(true)
  }

  const handleEdit = (post: NewsPost) => {
    setEditingPost(post)
    setModalOpen(true)
  }

  const renderPost = (post: NewsPost, isHighlighted: boolean) => (
    <NewsFeedItem
      key={post.id}
      post={post}
      isUnread={isNewsPostUnread(post, lastReadAtSnapshot)}
      isAdmin={isAdmin}
      isHighlighted={isHighlighted}
      onSetPinned={setPinned}
      onEdit={handleEdit}
      onDelete={handleDelete}
    />
  )

  const renderDeepLinkedPost = () => {
    // isFetched rather than isInitialLoading: v4 reports isFetching false for a query that is
    // merely paused (offline), which would otherwise read as "this post does not exist".
    if (deepLinkedPostId !== null && !deepLinkedPost.isFetched && !deepLinkedPost.isError) {
      return (
        <div className="news-feed__state" data-testid="news-deep-link-loading">
          <i className="fal fa-spinner fa-spin fa-2x" />
        </div>
      )
    }

    // A malformed id, a deleted post and one this reader may not see are one and the same outcome:
    // the message never says which, and the feed below still renders.
    if (deepLinkedPostId === null || deepLinkedPost.isError || !deepLinkedPost.data) {
      return (
        <div className="news-feed__state" data-testid="news-post-unavailable">
          <p>{t('news.postNotAvailable')}</p>
        </div>
      )
    }

    return (
      <div className="news-feed__list news-feed__list--highlighted">
        {renderPost(deepLinkedPost.data, true)}
      </div>
    )
  }

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="news-feed__state" data-testid="news-loading">
          <i className="fal fa-spinner fa-spin fa-2x" />
        </div>
      )
    }

    // Only a feed with nothing on screen becomes the error state: a failed `fetchNextPage` also
    // sets isError while keeping the pages already loaded, and those must not be thrown away.
    if (isError && posts.length === 0) {
      return (
        <div className="news-feed__state" data-testid="news-error">
          <p>{t('news.loadFailed')}</p>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={refetch}
            disabled={isFetching}
            data-testid="news-retry"
          >
            {t('news.retry')}
          </button>
        </div>
      )
    }

    // Counted over every loaded post, not over feedPosts: when the deep-linked post is the only one
    // there is, "no news posts yet" must not appear directly beneath it.
    if (posts.length === 0) {
      return (
        <div className="news-feed__state" data-testid="news-empty">
          <p>{t('news.empty')}</p>
        </div>
      )
    }

    return (
      <>
        <div className="news-feed__list">
          {feedPosts.map((post) => renderPost(post, false))}
        </div>
        {isError && (
          <p className="news-feed__load-more-error" data-testid="news-load-more-error">
            {t('news.loadMoreFailed')}
          </p>
        )}
        {hasMore && (
          <div className="news-feed__load-more">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={loadMore}
              disabled={isFetchingNextPage}
              data-testid="news-load-more"
            >
              {t('news.loadMore')}
            </button>
          </div>
        )}
      </>
    )
  }

  return (
    <div className="news-feed">
      <NewsTitleBar isAdmin={isAdmin} onAddClick={handleAddClick} />
      <div className="news-feed__content">
        {hasDeepLink && renderDeepLinkedPost()}
        {renderContent()}
      </div>
      {isModalOpen && (
        <NewsFormModal
          key={editingPost?.id ?? 'new'}
          show={isModalOpen}
          post={editingPost}
          onCreate={addPost}
          onUpdate={updatePost}
          onInvalidate={invalidateNews}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  )
}

export default NewsFeed
